<template>
  <section class="expenses-filters">
    <div class="section-title">
      <span @click="isOpen = !isOpen">
        {{ $t('expenses.listing.filters.title') }}
        <font-awesome-icon :icon="isOpen ? 'sort-up' : 'sort-down'" />
      </span>
    </div>

    <div v-show="isOpen">
      <div class="filter-inputs">
        <div class="date-range-label">
          {{ $t('expenses.listing.filters.dateRangeLabel') | uppercase }}
        </div>
        <div class="select-row">
          <div class="date-range">
            <DatePicker
              :value="startDate"
              :placeholder="$t('expenses.listing.filters.startRange')"
              @change="onStartDateChange"
            />
          </div>
          <div class="date-range">
            <DatePicker
              :value="endDate"
              :placeholder="$t('expenses.listing.filters.endRange')"
              @change="onEndDateChange"
            />
          </div>
        </div>
        <div class="select-inputs">
          <TolSelect
            v-if="canFilterContractor"
            :options="contractorsForSelect"
            :selected="selectedContractors"
            :placeholder="$t('expenses.listing.filters.contractor.all')"
            @change="onContractorChange"
          />

          <TolSelect
            v-if="canFilterAssignment"
            :options="assignmentsForSelect"
            :selected="selectedAssignments"
            :placeholder="$t('expenses.listing.filters.assignment.all')"
            @change="onAssignmentChange"
          />

          <TolSelect
            :options="statusesForSelect"
            :selected="selectedStatuses"
            :placeholder="$t('expenses.listing.filters.status.all')"
            :multi-select-label="$t('expenses.listing.filters.status.plural')"
            multiple
            @change="onStatusChange"
          />
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import DatePicker from '@/app/components/DatePicker';
import TolSelect from '@/app/components/TolSelect';

import format from 'date-fns/format';

export default {
  name: 'ExpensesFilters',
  components: {
    DatePicker,
    TolSelect
  },
  props: {
    expensesAssignments: {
      type: Array,
      required: true
    },
    expensesContractors: {
      type: Array,
      required: true
    },
    currentUser: {
      type: Object,
      required: true
    },
    expensesFilters: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      isOpen: false
    };
  },
  computed: {
    startDate() {
      return this.expensesFilters.dateRangeFilters.startDate;
    },
    endDate() {
      return this.expensesFilters.dateRangeFilters.endDate;
    },
    statusesForSelect() {
      let returnValue = [];
      Object.keys(this.expensesFilters.statusFilters).forEach(s => {
        returnValue.push({
          label: this.$t('statuses.' + s),
          value: s
        });
      });
      return returnValue;
    },
    selectedStatuses() {
      return this.statusesForSelect.filter(e => this.expensesFilters.statusFilters[e.value]);
    },
    canFilterContractor() {
      return this.currentUser.permissions['CONTRACTOR']
        ? this.currentUser.permissions['CONTRACTOR']['FILTER']
        : false;
    },
    canFilterAssignment() {
      return this.currentUser.permissions['ASSIGNMENT']
        ? this.currentUser.permissions['ASSIGNMENT']['FILTER']
        : false;
    },

    contractorsForSelect() {
      let returnValue = [];
      this.expensesContractors.forEach(c => {
        returnValue.push({
          label: c.last_name + ', ' + c.first_name,
          value: c.id
        });
      });
      return returnValue;
    },
    selectedContractors() {
      return this.contractorsForSelect.filter(e => e.value === this.expensesFilters.contractorId);
    },
    assignmentsForSelect() {
      let returnValue = [];
      this.expensesAssignments.forEach(a => {
        if (this.currentUser.id === a.contractorId) {
          returnValue.push({
            label: a.clientName,
            value: a.id
          });
        }
      });
      return returnValue;
    },
    selectedAssignments() {
      return this.assignmentsForSelect.filter(e => e.value === this.expensesFilters.assignmentId);
    }
  },
  methods: {
    formatDate(d) {
      return d ? format(d, 'YYYY-MM-DD') : null;
    },

    onStartDateChange(newValue) {
      this.$emit('change', {
        ...this.expensesFilters,
        dateRangeFilters: {
          ...this.expensesFilters.dateRangeFilters,
          startDate: newValue
        }
      });
    },

    onEndDateChange(newValue) {
      this.$emit('change', {
        ...this.expensesFilters,
        dateRangeFilters: {
          ...this.expensesFilters.dateRangeFilters,
          endDate: newValue
        }
      });
    },

    onContractorChange: function(contractorId) {
      if (!contractorId) contractorId = 0;
      this.$emit('change', { ...this.expensesFilters, contractorId });
    },

    onAssignmentChange: function(assignmentId) {
      if (!assignmentId) assignmentId = 0;
      this.$emit('change', { ...this.expensesFilters, assignmentId });
    },

    onStatusChange: function(multiSelectValue) {
      this.$emit('change', {
        ...this.expensesFilters,
        statusFilters: {
          draft: multiSelectValue.includes('draft'),
          submitted: multiSelectValue.includes('submitted'),
          approved: multiSelectValue.includes('approved'),
          processed: multiSelectValue.includes('processed'),
          rejected: multiSelectValue.includes('rejected'),
          obsolete: multiSelectValue.includes('obsolete')
        }
      });
    }
  }
};
</script>

<style lang="scss" scoped>
@import '../../../../../styles/colors.scss';
@import '../../../../../styles/variables.scss';

.expenses-filters {
  background-color: $background-level-2;

  .section-title {
    font-size: 22px;
    font-weight: bold;
    letter-spacing: -0.22px;
    line-height: 25px;
    margin-bottom: 20px;
    padding: 60px;
    display: flex;
    justify-content: space-between;

    > span {
      cursor: pointer;
    }
  }
  .filter-inputs {
    display: flex;
    flex-direction: column;
    padding: 40px;
    .date-range {
      margin-bottom: 12px;
    }

    .date-range-label {
      display: flex;
      margin-bottom: 12px;
    }

    .select-inputs {
      display: flex;
      flex-direction: column;
      .select-wrapper {
        margin-bottom: 12px;
      }
    }
  }

  .no-box {
    border: 0;
  }
}

@media only screen and (min-width: 768px) {
  .expenses-filters {
    .filter-inputs {
      .select-row {
        display: flex;
        flex-direction: row;
        .date-range,
        .select-wrapper {
          width: 40%;
          margin-right: 15px;
        }
      }

      .select-inputs {
        display: flex;
        flex-direction: row;
        .select-wrapper {
          width: 40%;
          margin-right: 15px;
        }
      }
    }
  }
}
</style>
